// Drafted notes (CINV-33): the note asking a contractor for a corrected invoice,
// and the reason an admin marks an invoice's flags as fine (CINV-27).
//
// A model may rewrite a draft on the request path, but everything it is allowed
// to say is decided here, from the stored verdict: the facts below are the only
// figures it is given, `acceptDraft` refuses any reply that adds one or talks
// about how the invoice was checked, and the templates are what every failure
// falls back to. A draft is never sent or saved by itself — an admin edits it.

import type { InvoiceCheck, InvoiceCheckId } from "./invoice-check.js";
import type { InvoiceValidationResult } from "./invoice-validation.js";

/**
 * Room for the note inside the contractor's notification, which is
 * `Your invoice for <month> needs a correction: <note>`, cut at 500.
 */
export const CORRECTION_DRAFT_MAX = 400;

/** Words that would tell the contractor how the invoice was checked (CINV-15). */
const BANNED_DRAFT_WORDS =
  /\b(AI|artificial|model|LLM|confidence|engine|algorithm|automated|automatically|software|flagged|verdict|validation|system)\b/i;

export type CorrectionFactKind =
  | "DAYS_OVER"
  | "HOURS_OVER"
  | "TOTAL"
  | "TAX"
  | "ARITHMETIC"
  | "PERIOD"
  | "DUPLICATE_NUMBER"
  | "NO_NUMBER"
  | "NO_ABN";

export type CorrectionFact = {
  kind: CorrectionFactKind;
  /** The sentence the template uses — also what the model is given to rewrite. */
  sentence: string;
  /** What the invoice says, and what it should say, as printed for a person. */
  invoiceValue: string | null;
  expectedValue: string | null;
};

export type CorrectionFacts = {
  /** "September 2026". */
  month: string;
  facts: CorrectionFact[];
};

const fact = (
  kind: CorrectionFactKind,
  sentence: string,
  c?: Pick<InvoiceCheck, "invoiceValue" | "expectedValue">
): CorrectionFact => ({
  kind,
  sentence,
  invoiceValue: c?.invoiceValue ?? null,
  expectedValue: c?.expectedValue ?? null,
});

/**
 * What a correction note may say, from the verdict: the claim against the
 * approved time (the user's call: an admin-sent note may state approved
 * figures, CINV-15), and the invoice's own figures that do not add up. Never
 * the model's confidence, its warnings, or anything about who issued it.
 */
export function correctionFactsOf(
  validation: Pick<InvoiceValidationResult, "checks"> | null,
  month: string
): CorrectionFacts {
  const checks = validation?.checks ?? [];
  const find = (id: InvoiceCheckId) => checks.find((c) => c.id === id);
  const facts: CorrectionFact[] = [];

  const days = find("DAYS");
  const hrs = find("HOURS");
  if (days?.result === "MISMATCH" && (days.delta ?? 0) > 0 && days.invoiceValue && days.expectedValue) {
    facts.push(
      fact(
        "DAYS_OVER",
        `The invoice claims ${days.invoiceValue} days for ${month}, but ${days.expectedValue} days are approved in your timesheets. Please re-issue it for ${days.expectedValue} days.`,
        days
      )
    );
  } else if (hrs?.result === "MISMATCH" && (hrs.delta ?? 0) > 0 && hrs.invoiceValue && hrs.expectedValue) {
    facts.push(
      fact(
        "HOURS_OVER",
        `The invoice claims ${hrs.invoiceValue} hours for ${month}, but ${hrs.expectedValue} hours are approved in your timesheets. Please re-issue it for ${hrs.expectedValue} hours.`,
        hrs
      )
    );
  }

  // CINV-30: when a key figure read is not on the PDF, the sums that disagree
  // may be the model's misreading, not the contractor's mistake — never ask
  // them to fix a figure their PDF has right. The admin checks it first.
  const misread = find("GROUNDING")?.result === "REVIEW";
  const total = misread ? undefined : find("TOTAL");
  if (total?.result === "MISMATCH")
    facts.push(
      fact(
        "TOTAL",
        total.invoiceValue && total.expectedValue
          ? `The total shown (${total.invoiceValue}) does not match the subtotal plus GST (${total.expectedValue}).`
          : "The total does not match the subtotal plus GST.",
        total
      )
    );
  const tax = misread ? undefined : find("TAX");
  if (tax?.result === "MISMATCH")
    facts.push(
      fact(
        "TAX",
        tax.invoiceValue && tax.expectedValue
          ? `The GST shown (${tax.invoiceValue}) should be ${tax.expectedValue}.`
          : "The GST amount is not right.",
        tax
      )
    );
  const lines = misread ? undefined : find("ARITHMETIC");
  if (lines?.result === "MISMATCH")
    facts.push(fact("ARITHMETIC", "The line amounts do not add up. Please check each line's quantity, rate and amount.", lines));
  const period = find("PERIOD");
  if (period?.result === "MISMATCH")
    facts.push(
      fact(
        "PERIOD",
        period.invoiceValue
          ? `The period on the invoice (${period.invoiceValue}) is outside ${month}. Please re-issue it for ${month}.`
          : `The period on the invoice is outside ${month}. Please re-issue it for ${month}.`,
        period
      )
    );
  const dup = find("DUP_NUMBER");
  if (dup?.result === "MISMATCH")
    facts.push(fact("DUPLICATE_NUMBER", "This invoice number is already on another of your invoices. Please re-issue it with a new number."));
  else if (dup?.result === "UNVERIFIABLE")
    facts.push(fact("NO_NUMBER", "Please add an invoice number."));
  const taxId = find("TAX_ID");
  if (taxId?.result === "UNVERIFIABLE" && taxId.field === "supplierAbn")
    facts.push(fact("NO_ABN", "Please include your ABN."));

  return { month, facts };
}

/** The note every failure falls back to — and the one shown with no model configured. */
export function templateCorrectionNote({ month, facts }: CorrectionFacts): string {
  if (facts.length === 0) return `Please check your invoice for ${month} and upload a corrected copy.`;
  const body = facts.map((f) => f.sentence).join(" ");
  const note = /upload/i.test(body) ? body : `${body} Please upload a corrected invoice.`;
  return clip(note);
}

function clip(text: string): string {
  if (text.length <= CORRECTION_DRAFT_MAX) return text;
  const cut = text.slice(0, CORRECTION_DRAFT_MAX);
  const lastStop = cut.lastIndexOf(". ");
  return lastStop > 80 ? cut.slice(0, lastStop + 1) : `${cut.slice(0, CORRECTION_DRAFT_MAX - 1)}…`;
}

/** Every figure in a text, normalised: "1,234.50" and "$1,234.50" are both "1234.5". */
function figuresIn(text: string): string[] {
  return (text.match(/\d[\d,]*(?:\.\d+)?/g) ?? []).map((n) => String(Number(n.replace(/,/g, ""))));
}

/**
 * Whether a model's draft may be offered: short enough, says nothing about how
 * the invoice was checked, and carries no figure the facts did not give it.
 * The templates pass by construction; a test holds them to it.
 */
export function acceptDraft(text: string, allowed: string, max = CORRECTION_DRAFT_MAX): boolean {
  const trimmed = text.trim();
  if (trimmed.length < 3 || trimmed.length > max) return false;
  if (BANNED_DRAFT_WORDS.test(trimmed)) return false;
  const known = new Set(figuresIn(allowed));
  return figuresIn(trimmed).every((n) => known.has(n));
}

/** Everything a correction draft may quote, as one string for `acceptDraft`. */
export const correctionFactText = ({ month, facts }: CorrectionFacts) =>
  [month, ...facts.flatMap((f) => [f.sentence, f.invoiceValue ?? "", f.expectedValue ?? ""])].join(" ");

// --- Marking an invoice OK (CINV-27) -------------------------------------------

export const EXPLANATION_PRESET_IDS = [
  "HOURS_CONFIRMED",
  "RATE_AGREED",
  "EXTRAS_AGREED",
  "ROUNDING",
  "PRE_PORTAL",
  "FIGURES_CHECKED",
] as const;
export type ExplanationPresetId = (typeof EXPLANATION_PRESET_IDS)[number];

export type ExplanationPreset = {
  id: ExplanationPresetId;
  /** The chip's label. */
  label: string;
  /** What is saved as the explanation. */
  text: string;
  /** The checks it answers; FIGURES_CHECKED answers any. */
  checks: readonly InvoiceCheckId[];
};

export const EXPLANATION_PRESETS: readonly ExplanationPreset[] = [
  {
    id: "HOURS_CONFIRMED",
    label: "Hours confirmed",
    text: "Hours confirmed with the contractor's manager.",
    checks: ["WEEKS", "HOURS", "DAYS", "HOURS_BY_PROJECT"],
  },
  { id: "RATE_AGREED", label: "Rate agreed", text: "Rate agreed outside the portal.", checks: ["RATE", "UNIT"] },
  { id: "EXTRAS_AGREED", label: "Extras agreed", text: "Extra charges were agreed in advance.", checks: ["EXPENSES"] },
  {
    id: "ROUNDING",
    label: "Rounding only",
    text: "Rounding difference only — fine to pay.",
    checks: ["TOTAL", "TAX", "ARITHMETIC", "SPLIT"],
  },
  {
    id: "PRE_PORTAL",
    label: "Before the portal",
    text: "Paid before timesheets were kept in the portal.",
    checks: ["WEEKS", "HOURS", "DAYS"],
  },
  {
    id: "FIGURES_CHECKED",
    label: "Checked the PDF",
    text: "Checked against the PDF — the figures are right.",
    checks: [],
  },
];

const flagged = (checks: readonly InvoiceCheck[]) =>
  checks.filter((c) => c.result === "MISMATCH" || c.result === "REVIEW" || c.result === "UNVERIFIABLE");

/** The presets worth offering for these checks, most relevant first; "Checked the PDF" always last. */
export function presetsFor(checks: readonly InvoiceCheck[]): ExplanationPreset[] {
  const ids = new Set(flagged(checks).map((c) => c.id));
  const relevant = EXPLANATION_PRESETS.filter(
    (p) => p.id !== "FIGURES_CHECKED" && p.checks.some((id) => ids.has(id))
  );
  return [...relevant, EXPLANATION_PRESETS.find((p) => p.id === "FIGURES_CHECKED")!];
}

/** The reason to suggest with no model to ask: the likeliest preset for the first flag. */
export function templateExplanation(checks: readonly InvoiceCheck[]): ExplanationPreset {
  const flags = flagged(checks);
  const money = flags.find((c) => ["TOTAL", "TAX", "ARITHMETIC", "SPLIT"].includes(c.id));
  if (money && money.result === "MISMATCH" && money.delta !== null && Math.abs(money.delta) < 1)
    return EXPLANATION_PRESETS.find((p) => p.id === "ROUNDING")!;
  for (const preset of presetsFor(checks)) {
    if (preset.id === "ROUNDING" || preset.id === "PRE_PORTAL") continue;
    if (preset.checks.some((id) => flags.some((c) => c.id === id))) return preset;
  }
  return EXPLANATION_PRESETS.find((p) => p.id === "FIGURES_CHECKED")!;
}

/** Wire shapes for the two model calls. Strict, every key required. */
export const CORRECTION_DRAFT_JSON_SCHEMA = {
  type: "object",
  properties: { note: { type: "string" } },
  required: ["note"],
  additionalProperties: false,
} as const;

export const EXPLANATION_DRAFT_JSON_SCHEMA = {
  type: "object",
  properties: {
    presetId: { type: "string", enum: [...EXPLANATION_PRESET_IDS, "OTHER"] },
    note: { type: "string" },
  },
  required: ["presetId", "note"],
  additionalProperties: false,
} as const;

export const CORRECTION_DRAFT_PROMPT = `You write a short note from an accounts administrator to a contractor, asking them to fix their invoice and upload a corrected copy.
Rules:
1. Use only the facts given. Never add a figure, date or name that is not in them.
2. Never mention software, AI, a model, automation, confidence, or how the invoice was checked.
3. Plain, polite Australian English. No greeting and no sign-off — the email adds both.
4. At most ${CORRECTION_DRAFT_MAX} characters. Say each problem once, then ask for a corrected invoice.
Respond with a single JSON object {"note": "..."} and nothing else.`;

export const EXPLANATION_DRAFT_PROMPT = `You help an accounts administrator record why the issues found on a contractor's invoice are acceptable, so it can be paid.
Pick the one preset that best fits the issues, by its id. If none fits, use "OTHER" and write a short note (at most 200 characters) in plain English, using no figure that is not in the issues.
Respond with a single JSON object {"presetId": "...", "note": "..."} and nothing else. For a preset, "note" may be empty.`;

/** What the explanation draft is told about the issues: ids and figures, no sentences, no names. */
export function explanationIssues(checks: readonly InvoiceCheck[]): string {
  return JSON.stringify(
    flagged(checks).map((c) => ({
      check: c.id,
      result: c.result,
      invoice: c.invoiceValue,
      expected: c.expectedValue,
      difference: c.delta,
    }))
  );
}

export type InvoiceNoteDraft = {
  note: string;
  /** Who wrote it: the model, or the template it fell back to. */
  source: "AI" | "TEMPLATE";
  /** For an explanation: the preset it is, when it is one. */
  presetId?: ExplanationPresetId | null;
};
